import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {type MailDetails, MailDetailsWithMIME, MPAMailRepositoryApi} from "components/src/api/admin/generated";
import {ClearSelection} from "components/src/common/ClearSelection";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {InvertSelection} from "components/src/common/InvertSelection";
import {Pager} from "components/src/common/Pager";
import {RowsPerPage} from "components/src/common/RowsPerPage";
import {Truncate} from "components/src/common/Truncate";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageAlert} from "components/src/layout/PageAlert.tsx";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {PageFooter} from "components/src/layout/PageFooter.tsx";
import {PageHeader} from "components/src/layout/PageHeader.tsx";
import {DateUtils} from "components/src/utils/DateUtils";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {AtSign as MTAIcon, Bin as DeleteIcon, Redo as RequeueIcon} from "iconoir-react";
import {useEffect, useState} from 'react'
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Nav from "react-bootstrap/Nav";
import Navbar from 'react-bootstrap/Navbar';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import {useLocation, useNavigate} from "react-router-dom";
import {MPAViewMailDialog} from "./MPAViewMailDialog";

/*
 * REST api for accessing the back-end
 */
const mailRepositoryApi = new MPAMailRepositoryApi(ApiConfig.config());

type OnClick = () => void;

interface ColumnProps {
    value?: string
    children?: React.ReactNode
    className? : string
    maxLength? : number
    onClick? : OnClick
}

const Column = ({value, children, className, maxLength = 60, onClick}: ColumnProps) => {
    return (
        <td onClick={onClick} className={"user-select-all cursor-pointer " + className}>{children}<Truncate value={value} maxLength={maxLength}/></td>
    )
}

export const MPARepository = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * Represents the current location object in a React Router application.
     */
    const location = useLocation()
    /**
     * The mail repository URLs
     */
    const [mailRepositoryUrls, setMailRepositoryUrls] = useState<string[]>();
    /**
     * The selected mail queue name
     */
    const [selectedMailRepositoryUrl, setSelectedMailRepositoryUrl] = useState<string>(location?.state?.selectedMailRepositoryUrl || undefined);
    /**
     * The mail keys
     */
    const [keys, setKeys] = useState<string[]>([]);
    /**
     * The mail queue items
     */
    const [queueItems, setQueueItems] = useState<MailDetails[]>([]);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the first page to show
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of domains to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * The selected queue item
     */
    const [selectedQueueItems, setSelectedQueueItems] = useState<MailDetails[]>([]);
    /**
     * The downloaded Mail with MIME contents
     */
    const [mailWithMime, setMailWithMime] = useState<MailDetailsWithMIME>();
    /**
     * If true, the items will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the "mail dialog" modal dialog will be shown
     */
    const [showMailDialog, setShowMailDialog] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    useEffect(() =>
    {
        logger.debug("Load mail repository urls")

        // reset state when we load new data
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mailRepositoryApi.mailRepositoryControllerGetMailRepositoryUrls()
            .then(response =>
            {
                const urls = response.data;

                setMailRepositoryUrls(urls);

                if (selectedMailRepositoryUrl === undefined && urls !== undefined && urls.length > 0) {
                    setSelectedMailRepositoryUrl(urls[0])
                }
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting mail repository urls", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        selectedMailRepositoryUrl,
        t
    ]);


    useEffect(() =>
    {
        if (selectedMailRepositoryUrl === undefined) {
            return;
        }

        logger.debug("Load repository keys")

        // reset state when we load new data
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mailRepositoryApi.mailRepositoryControllerGetMailKeys(selectedMailRepositoryUrl)
            .then(response => {
                setKeys(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting repository keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        selectedMailRepositoryUrl,
        t
    ]);

    useEffect(() =>
    {
        if (selectedMailRepositoryUrl === undefined || keys?.length === 0) {
            return;
        }

        logger.debug("Load repository items")

        // reset state when we load new data
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mailRepositoryApi.mailRepositoryControllerGetMails(selectedMailRepositoryUrl, keys.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage))
            .then(response => {
                setQueueItems(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting repository list", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        selectedMailRepositoryUrl,
        keys,
        t
    ]);

    const onChangeSelect = (checked : boolean, queueItem : MailDetails) =>
    {
        setSelectedQueueItems(checked ?
            [...selectedQueueItems, queueItem] :
            selectedQueueItems.filter(q => q.name !== queueItem.name)
        );
    }

    const onClickMailname = (queueItem : MailDetails) =>
    {
        if (selectedMailRepositoryUrl === undefined) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mailRepositoryApi.mailRepositoryControllerGetMail(selectedMailRepositoryUrl, queueItem.name, true)
            .then((result) => {
                setMailWithMime(result.data)
                setShowMailDialog(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error downloading queue item", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }

    const invertSelection = () => {
        setSelectedQueueItems([...queueItems].filter(q => !selectedQueueItems.includes(q)));
    }

    const reload = () =>
    {
        setQueueItems([]);
        setKeys([]);
        setForceReload(true);
    }

    const deleteSelected = () =>
    {
        if (selectedMailRepositoryUrl === undefined) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mailRepositoryApi.mailRepositoryControllerDeleteMails(selectedMailRepositoryUrl, selectedQueueItems.map(q => q.name))
            .then(() => {
                reload();
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting queue items", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setShowDeleteConfirm(false);
                setSelectedQueueItems([]);
            })
    }

    const requeueSelected = () =>
    {
        if (selectedMailRepositoryUrl === undefined) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mailRepositoryApi.mailRepositoryControllerRequeueMails(selectedMailRepositoryUrl, selectedQueueItems.map(q => q.name))
            .then(() => {
                reload();
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error re-queueing items", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setShowDeleteConfirm(false);
                setSelectedQueueItems([]);
            })
    }

    /**
     * Is called when the View Mail Dialog is closed
     */
    const onMPAViewMailDialogClose = () => {
        setShowMailDialog(false);
    }

    return (
        <>
            <ConfirmDialog title={t("common.confirm")}
                           bodyText={t("mpa-repository.body-text")}
                           confirmButtonTitle={t("mpa-repository.delete")}
                           onClickConfirmEvent={deleteSelected}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {showMailDialog && mailWithMime !== undefined && <MPAViewMailDialog mailWithMIME={mailWithMime} onClose={onMPAViewMailDialogClose} />}

            <div className="d-flex flex-column h-100">
                <PageHeader icon={<MTAIcon />} title={t("mpa-repository.title")} onClickReload={() => setForceReload(true)}/>

                <PageAlert message={errorMessage} setMessage={setErrorMessage}/>

                <div className="px-4">
                    <Row>
                        <div className="d-flex flex-row align-items-center mb-4">
                            <div className="d-flex justify-content-start">
                                <Form.Select size="sm" value={selectedMailRepositoryUrl} onChange={(e) => setSelectedMailRepositoryUrl(e.target.value)}>
                                    {mailRepositoryUrls?.map(url => (
                                        <option key={url} value={url}>{url}</option>
                                    ))}
                                </Form.Select>
                            </div>
                        </div>
                    </Row>
                </div>

                <PageContent className="flex-grow-1 min-h-0">
                    <Row className="h-100 w-100">
                        <Col className="h-100 overflow-auto">
                            <Table className="sticky sticky-x" bordered hover>
                                <thead className="sticky-top">
                                <tr className="text-nowrap">
                                    {selectedQueueItems.length === 0 ?
                                        <>
                                            <th className="th-w-min"><InvertSelection invertSelectionEvent={invertSelection}/></th>
                                            <th>{t("mpa-repository.name")}</th>
                                            <th>{t("mpa-repository.recipients")}</th>
                                            <th>{t("mpa-repository.sender")}</th>
                                            <th>{t("mpa-repository.state")}</th>
                                            <th>{t("mpa-repository.remote-host")}</th>
                                            <th>{t("mpa-repository.remote-address")}</th>
                                            <th>{t("mpa-repository.error-message")}</th>
                                            <th>{t("mpa-repository.message-size")}</th>
                                            <th>{t("mpa-repository.last-updated")}</th>
                                        </>
                                        :
                                        <th colSpan={10}>
                                            <Navbar className="bg-body-tertiary ps-2">
                                                {/* Add icon to first column which will clear the selection when clicked */}
                                                <ClearSelection clearSelectionEvent={() => setSelectedQueueItems([])} checked={true}/>

                                                <Navbar.Text className="ms-2">{selectedQueueItems.length} selected</Navbar.Text>

                                                <div className="vr ms-4 me-2"></div>

                                                <Nav>
                                                    <Nav.Link disabled={selectedQueueItems.length === 0} onClick={() => setShowDeleteConfirm(true)}>
                                                        <DeleteIcon className="text-danger"/> {t("mpa-repository.delete-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link disabled={selectedQueueItems.length === 0} onClick={() => requeueSelected()}>
                                                        <RequeueIcon className="text-success"/> {t("mpa-repository.requeue-selected")}
                                                    </Nav.Link>
                                                </Nav>
                                            </Navbar>
                                        </th>
                                    }
                                </tr>
                                </thead>
                                <tbody>
                                {queueItems?.map((q: MailDetails) => (
                                    <tr key={q.name} className="text-nowrap">
                                        <td className="td-w-min">
                                            <Form.Check
                                                type="checkbox"
                                                className="me-0"
                                                checked={selectedQueueItems.includes(q)}
                                                onChange={(e) => onChangeSelect(e.target.checked, q)}
                                                inline
                                            />
                                        </td>
                                        <Column value={q.name} onClick={() => onClickMailname(q)}/>
                                        <Column value={q.recipients.join(", ")} onClick={() => onClickMailname(q)}/>
                                        <Column value={q.sender} onClick={() => onClickMailname(q)}/>
                                        <Column value={q.state} onClick={() => onClickMailname(q)}/>
                                        <Column value={q.remoteHost} onClick={() => onClickMailname(q)}/>
                                        <Column value={q.remoteAddr} onClick={() => onClickMailname(q)}/>
                                        <Column value={q.errorMessage} onClick={() => onClickMailname(q)}/>
                                        <Column value={`${q.messageSize}`} onClick={() => onClickMailname(q)}/>
                                        <Column value={DateUtils.fromTimestampToLocaleDateStringShort(q.lastUpdated)} onClick={() => onClickMailname(q)}/>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </PageContent>

                <PageFooter
                    left={<RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} setCurrentPage={setCurrentPage}/>}
                    right={<Pager itemCount={keys.length ?? 0}
                                  currentPage={currentPage}
                                  setCurrentPage={setCurrentPage}
                                  maxItemsPerPage={rowsPerPage}
                    />}
                />
            </div>
        </>
    );
}
